<template>
    <div>
        <ResultDialogCSV
            :token="token || ''"
            :show="uploadResultDialog"
            :headers="csvHeaders"
            :result="resultadoUpload"
            @close="closeResultDialog"
            @save="saveResult"
        ></ResultDialogCSV>

        <v-toolbar flat color="white" class="cadastro-toolbar" style="border-radius: 10px;">
            <v-toolbar-title class="cadastro-toolbar__title" style="color: #4a4a4a">{{ title }}</v-toolbar-title>

            <v-spacer />

            <v-text-field
                v-if="hasSearchField"
                v-model="search"
                class="cadastro-toolbar__search"
                append-icon="mdi-magnify"
                :label="$t('system.labels.search')"
                single-line
                hide-details
                @input="$emit('search', search)"
                @blur="$emit('search', search)"
                :color="search.length > 0 ? 'blue' : 'black'"
            ></v-text-field>

            <div v-if="$slots.extension" class="cadastro-toolbar__extension">
                <slot name="extension"></slot>
            </div>
            <div
                v-if="hasActionBtns || hasAddSlot"
                class="cadastro-toolbar__actions"
            >
                <HelpDialog 
                    v-if="hasHelpDialog"
                    :items="helpDialogItems"
                    :intro="helpDialogIntro"
                    :title="helpDialogTitle"
                    size="s"
                />
    
                <ExportTableCSV
                    v-if="hasExportCsv"
                    :title="csvTitle"
                    :content="items"
                    :content-loader="loadItemsForExport"
                    :defer-processing="backgroundExport"
                    :headers="resolvedExportHeaders"
                    :local-row-limit="csvLocalRowLimit"
                    :list-endpoint="listEndpoint"
                    :export-filters="exportFilters"
                    :request-params="requestParams"
                    :post-filters="postFilters"
                    :export-sort-by="exportSortBy"
                    :export-sort-desc="exportSortDesc"
                    :export-total="exportTotal"
                    :selected-ids="selectedIds"
                    :id-field="idField"
                    @export-loading="$emit('export-loading', $event)"
                />

                <ExportTablePdf 
                    v-if="hasExportPdf"
                    :title="pdfTitle"
                    :subtitle="pdfSubtitle"
                    :content="items"
                    :content-loader="loadItemsForExport"
                    :defer-processing="backgroundExport"
                    :headers="resolvedExportHeaders"
                    :filename="pdfFilename"
                    @export-loading="$emit('export-loading', $event)"
                />
                
                <HelpDialogCSV 
                    v-if="hasUploadCSV"
                    :fields="helpDialogCsvFields"
                    :model="helpDialogCsvModel"
                    :data="items"
                    :data-loader="loadItemsForExport"
                    :local-row-limit="csvLocalRowLimit"
                    :list-endpoint="listEndpoint"
                    :export-filters="exportFilters"
                    :request-params="requestParams"
                    :post-filters="postFilters"
                    :export-sort-by="exportSortBy"
                    :export-sort-desc="exportSortDesc"
                    :export-total="exportTotal"
                    :selected-ids="selectedIds"
                    :id-field="idField"
                    :export-formats="csvExportFormats"
                    size="s"
                    @export-loading="$emit('export-loading', $event)"
                />
                
                <UploadDialog 
                    v-if="hasUploadCSV"
                    :user="user"
                    :token="token || ''"
                    :endpoint="endpointUploadCsv"
                    :templates="templatesCsv"
                    size="s"
                    @done="openResultDialog"
                />
                
                <v-tooltip top v-if="showDownloadQR">
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" color="primary" dark small class="mr-2" fab @click="downloadQRDialogOpen = true">
                            <v-icon>mdi-qrcode</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t('equipment.qrcode.downloadTitle') }}</span>
                </v-tooltip>
                <DownloadQRDialog v-model="downloadQRDialogOpen" :token="token || ''" />

                <slot name="add"></slot>
            </div>
        </v-toolbar>
    </div>
</template>

<script>
import HelpDialog from '../Dialogs/HelpDialog.vue';
import HelpDialogCSV from '../Dialogs/HelpDialogCSV.vue';
import UploadDialog from '../Dialogs/UploadDialog.vue';
import ExportTableCSV from '../ExportTableCSV.vue';
import ExportTablePdf from '../ExportTablePdf.vue';
import ResultDialogCSV from '../Dialogs/ResultDialogCSV.vue';
import DownloadQRDialog from '../Dialogs/DownloadQRDialog.vue';

export default {
    name: "CadastroToolbar",
    components: {
        HelpDialog,
        HelpDialogCSV,
        UploadDialog,
        ExportTableCSV,
        ExportTablePdf,
        ResultDialogCSV,
        DownloadQRDialog
    },
    props: {
        token: {
            type: String,
            required: true
        },
        title: {
            type: String,
            required: true
        },
        user: {
            type: [Number, String, null],
            default() {
                return null
            }
        },
        hideSearch: {
            type: Boolean, 
            default() {
                return false
            }
        },
        helpDialogItems: {
            type: Array, 
            default() {
                return []
            }
        },
        helpDialogIntro: {
            type: [String, null], 
            default() {
                return null
            }
        },
        helpDialogTitle: {
            type: [String, null], 
            default() {
                return null
            }
        },
        items: {
            type: [Array, null],
            default() {
                return null
            }
        },
        csvTitle: {
            type: [String, null],
            default() {
                return null
            }
        },
        csvHeaders: {
            type: [Array, null],
            default() {
                return []
            }
        },
        exportHeaders: {
            type: [Array, null],
            default() {
                return null;
            }
        },
        pdfTitle: {
            type: [String, null],
            default() {
                return null
            }
        },
        pdfHeaders: {
            type: [Array, null],
            default() {
                return null
            }
        },
        pdfFilename: {
            type: [String, null],
            default() {
                return null
            }
        },
        pdfSubtitle: {
            type: [String, null],
            default() {
                return ''
            }
        },
        helpDialogCsvFields: {
            type: Array,
            default() {
                return []
            }
        },
        helpDialogCsvModel: {
            type: [String, null],
            default() {
                return null
            }
        },
        endpointUploadCsv: {
            type: [String, null],
            default() {
                return null
            }
        },
        templatesCsv: {
            type: [Array, null],
            default() {
                return null
            }
        },
        /**
         * Formatos de colunas para exportação do template/dados CSV (HelpDialogCSV).
         * Ex.: [{ value: 'legacy', text: '...', excludeKeys: ['cod_linha_ida'] }]
         */
        csvExportFormats: {
            type: Array,
            default: null,
        },
        showDownloadQR: {
            type: Boolean,
            default() {
                return false
            }
        },
        /** Se definido, CSV/PDF buscam todos os registros apenas na hora da exportação (evita carga junto da paginação). */
        loadItemsForExport: {
            type: Function,
            default: null
        },
        /** Exportação CSV/PDF sem travar a tela: download/geração em etapas com yields na UI. */
        backgroundExport: {
            type: Boolean,
            default: false
        },
        /** Path da API de listagem da grade (ex.: getallpessoas) para export CSV via scheduler. */
        listEndpoint: {
            type: String,
            default: null
        },
        exportFilters: {
            type: Object,
            default() {
                return {};
            }
        },
        /** Parâmetros (não SQL) para re-chamar o listEndpoint sem paginação server-side (ex.: { dti, dtf }). */
        requestParams: {
            type: Object,
            default() {
                return {};
            }
        },
        /** Filtros aplicados pelo worker sobre a resposta do listEndpoint (contains/from-to/in/eq). */
        postFilters: {
            type: Object,
            default() {
                return {};
            }
        },
        exportSortBy: {
            type: Array,
            default() {
                return [];
            }
        },
        exportSortDesc: {
            type: Array,
            default() {
                return [];
            }
        },
        exportTotal: {
            type: Number,
            default: 0
        },
        selectedIds: {
            type: Array,
            default() {
                return [];
            }
        },
        idField: {
            type: String,
            default: null
        }
    },
    data: () => ({
        uploadResultDialog: false, 
        search: "",
        resultadoUpload: {
            error: true,
            arqlog: "",
            result: []
        },
        defaultResultadoUpload: {
            error: true,
            arqlog: "",
            result: []
        },
        downloadQRDialogOpen: false,
    }),
    computed: {
        hasSearchField() {
            return !this.hideSearch;
        },
        hasAddSlot() {
            return !!(this.$slots.add || this.$scopedSlots.add);
        },
        hasActionBtns() {
            return this.hasHelpDialog || this.hasExportCsv || this.hasExportPdf || this.hasUploadCSV || this.showDownloadQR;
        },
        hasHelpDialog() {
            return this.helpDialogItems.length > 0;
        },
        hasExportCsv() {
            return this.items !== null && this.csvTitle !== null;
        },
        hasExportPdf() {
            return this.items !== null && this.pdfTitle !== null;
        },
        hasUploadCSV() {
            return this.endpointUploadCsv !== null;
        },
        resolvedExportHeaders() {
            if (this.exportHeaders && this.exportHeaders.length > 0) {
                return this.exportHeaders;
            }
            if (this.pdfHeaders && this.pdfHeaders.length > 0) {
                return this.pdfHeaders;
            }
            return this.csvHeaders || [];
        },
        csvLocalRowLimit() {
            const limite = Number(this.$store.getters['config/limiteLinhasExportacaoCsvLocal']);
            return Number.isInteger(limite) && limite > 0 ? limite : 1000;
        },
    },
    methods: {
        openResultDialog(dados) {
            this.resultadoUpload = Object.assign({}, dados);
            this.uploadResultDialog = true;
        },
        closeResultDialog() {
            this.resultadoUpload = Object.assign({}, this.defaultResultadoUpload);
            this.uploadResultDialog = false;
        },
        saveResult(result, fullData) {
            this.$emit('saveBulk', result, fullData);
            this.closeResultDialog();
        }
    }
}
</script>
<style scoped>
.cadastro-toolbar__title {
  min-width: 4rem;
  flex: 0 1 auto;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
/* Busca ocupa o espaço livre da linha, mas encolhe antes de forçar quebra */
.cadastro-toolbar__search.v-input {
  flex: 0 1 220px;
  min-width: 120px;
  max-width: 260px;
}
</style>
<style>
/* Linha única: título | busca | filtros | ações.
   Se não couber, as ações quebram inteiras para a linha de baixo
   (os filtros permanecem na primeira linha). Nunca há overflow. */
.cadastro-toolbar.v-toolbar {
  height: auto !important;
  min-height: 48px;
  overflow: visible;
}
.cadastro-toolbar .v-toolbar__content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 8px;
  row-gap: 4px;
  height: auto !important;
  min-height: 48px;
  padding-top: 4px;
  padding-bottom: 4px;
  overflow: visible;
}
.cadastro-toolbar .v-toolbar__content > .spacer {
  flex: 1 1 0;
  min-width: 0;
}
.cadastro-toolbar__extension {
  display: flex;
  flex-direction: row;
  /* último recurso: se só os filtros já estouram a largura, eles quebram
     entre si em vez de gerar scroll horizontal */
  flex-wrap: wrap;
  justify-content: flex-end;
  align-items: center;
  column-gap: 8px;
  row-gap: 4px;
  flex: 0 1 auto;
  min-width: 0;
}
.cadastro-toolbar__actions {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  /* quando cabe tudo, encosta à direita; quando quebra, mantém alinhado à direita */
  margin-right: auto;
  flex: 0 0 auto;
  min-width: 0;
}
/* v-dialog/v-tooltip no slot extension são block por padrão — força linha */
.cadastro-toolbar__extension > *,
.cadastro-toolbar__actions > * {
  flex: 0 0 auto;
  align-items: center;
  vertical-align: middle;
}
/* mb-2 nos botões desalinha verticalmente numa row centralizada */
.cadastro-toolbar__extension .v-btn.mb-2,
.cadastro-toolbar__actions .v-btn.mb-2 {
  margin-bottom: 0 !important;
}
.cadastro-toolbar__extension .v-input--switch {
  flex: 0 0 auto;
  margin-top: 0;
  padding-top: 0;
}
</style>